import { useState, type FormEvent } from 'react'
import HoldToTransmit from './HoldToTransmit'
import styles from './Outbox.module.css'
import { useT } from '../../../lib/i18n'

/**
 * The outbox IS the will-surface for a correspondent pack: the desk has no other
 * way to act on the world. One line, and it is ALWAYS live — §4.3: no mode,
 * pacing state or dark window may disable the composer. Transmitting into a dark
 * window is legal; the engine decides what reaches him and when.
 */
export default function Outbox({
  disabled,
  placeholder,
  onSend,
  value,
  onChange,
  onFocus,
}: {
  /** Only ever true when the session itself has ended. Never for pacing. */
  disabled?: boolean
  placeholder?: string
  /** Called with the text when uncontrolled; called bare when controlled. */
  onSend: (text: string) => void
  /**
   * CONTROLLED MODE (mobile). The phone surface owns the draft, because it hands
   * the same text to the compose surface when the keyboard opens and takes it
   * back when the keyboard closes — a draft that lived in here would be lost at
   * exactly the moment the player started writing in earnest.
   *
   * Absent, the box keeps its own text and nothing about the desktop surface
   * changes.
   */
  value?: string
  onChange?: (text: string) => void
  onFocus?: () => void
}) {
  const t = useT()
  const [own, setOwn] = useState('')
  const controlled = value !== undefined && onChange !== undefined
  const text = controlled ? value : own
  const setText = controlled ? onChange : setOwn

  function submit(ev: FormEvent) {
    ev.preventDefault()
    const v = text.trim()
    if (!v || disabled) return
    onSend(v)
    if (!controlled) setOwn('')
  }

  return (
    <form className={styles.bar} onSubmit={submit}>
      <div className={styles.inner}>
        <span className={styles.prompt} aria-hidden="true">
          ›
        </span>
        <input
          className={styles.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={onFocus}
          placeholder={placeholder ?? 'key the channel'}
          aria-label={t('desk.compose.keyAria')}
          autoComplete="off"
          disabled={disabled}
        />
        {/*
          Dictation lands here as editable text rather than going straight out —
          the operator reads it before it is on the wire. Appends, so a hold can
          add to something already typed.
        */}
        <HoldToTransmit
          disabled={disabled}
          // Reads the current value rather than an updater, because in controlled
          // mode `setText` is the parent's plain setter and takes a string.
          onTranscript={(t) => setText(text.trim() ? `${text.trim()} ${t}` : t)}
        />
      </div>
    </form>
  )
}
